import {
  Alert,
  Button,
  ButtonGroup,
  FormControl,
  FormLabel,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React, { useCallback, useEffect, useState } from 'react';

import { Form as FormWrapper } from 'src/components/common/form/Form';
import { Loading } from 'src/components/common/loading/Loading';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { MultiSelect } from 'src/components/multiSelect/MultiSelect';
import { TagList } from 'src/components/tagList/TagList';
import { DocsRouter } from 'src/lib/routers';

import {
  UsePQBatchLinkVariantsFailureMessage,
  usePQBatchLinkVariants,
} from '../../usePQBatchLinkVariants';

interface Props {
  graphId: string;
  listId: string;
  onClose: () => void;
  isOpen: boolean;
  __test?: {
    loading?: boolean;
    submitting?: boolean;
    failureMessages?: UsePQBatchLinkVariantsFailureMessage[];
  };
}

export const PersistedQueryListLinkUpdateModal = ({
  graphId,
  listId,
  onClose,
  isOpen,
  __test,
}: Props) => {
  const {
    batchLinkVariants,
    loading: batchLoading,
    submitting: batchSubmitting,
    errors,
    persistedQueryList,
    allVariants,
    linkedVariants,
    unlinkResults,
    linkResults,
    failureMessages: batchFailureMessages,
  } = usePQBatchLinkVariants({
    graphId,
    listId,
  });

  const loading = batchLoading || Boolean(__test?.loading);
  const submitting = batchSubmitting || Boolean(__test?.submitting);
  const failureMessages = __test?.failureMessages ?? batchFailureMessages;
  const [selectedVariants, setSelectedVariants] = useState<Set<string>>(
    new Set(),
  );
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!touched) {
      setSelectedVariants(new Set(linkedVariants?.map((v) => v.name) ?? []));
    }
  }, [linkedVariants, touched]);

  const handleVariantsChanged = useCallback(
    (variants: Set<string>) => {
      setSelectedVariants(variants);
      setTouched(true);
    },
    [setSelectedVariants, setTouched],
  );

  const handleSubmit = useCallback(async () => {
    await batchLinkVariants(new Set([...selectedVariants]));
  }, [selectedVariants, batchLinkVariants]);

  useEffect(() => {
    if (!loading && !submitting && (linkResults || unlinkResults)) {
      // we've just run a link / unlink mutation
      if (errors.length > 0 || failureMessages.length > 0) {
        return;
      }
      onClose();
    }
  }, [
    batchLinkVariants,
    loading,
    submitting,
    errors,
    allVariants,
    linkResults,
    unlinkResults,
    failureMessages,
    onClose,
  ]);

  return (
    <Modal size="xl" onClose={onClose} isOpen={isOpen}>
      <ModalOverlay />
      <FormWrapper onSubmit={handleSubmit} noValidate>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Link and Unlink Variants</ModalTitle>
            <ModalDescription>
              Linking to a variant allows persisted query IDs from the list{' '}
              <em>{persistedQueryList?.name}</em> to be published to your
              router. Once linked, you will be able to use persisted query
              features for any linked variants.{' '}
              <Link isExternal to={DocsRouter.path('PersistedQueries')}>
                Learn more
              </Link>
            </ModalDescription>
          </ModalHeader>
          <ModalBody isScrollable={false}>
            {/* TODO(tim): only show this banner when unlinking variants? */}
            <Alert status="warning">
              Unlinking variants from this list is a destructive action! Make
              sure your production variant isn’t using this list for persisted
              query lookups or to enforce a safelist before you proceed.
            </Alert>
            <FormControl>
              <FormLabel htmlFor="variant">Linked Variants</FormLabel>
              {loading ? (
                <Loading size="sm" />
              ) : (
                <MultiSelect
                  disabled={submitting}
                  className="h-auto max-h-96 w-120 max-w-full overflow-scroll"
                  valueTitle="Variants"
                  allValues={allVariants?.map((v) => v.name) ?? []}
                  selectedValues={selectedVariants}
                  setSelectedValues={handleVariantsChanged}
                  triggerNode={
                    <TagList
                      data-testid="pq-link-variant-taglist"
                      title="Select variant(s)"
                      tags={selectedVariants}
                      erroredTags={
                        new Set([
                          ...(failureMessages
                            .map((fm) => fm.variantName)
                            .filter((m) => m !== undefined) as string[]),
                        ])
                      }
                      onTagRemoved={(value) => {
                        const newSelectedVariants = new Set(selectedVariants);
                        newSelectedVariants.delete(value);
                        handleVariantsChanged(newSelectedVariants);
                      }}
                      disabled={false}
                    />
                  }
                />
              )}
            </FormControl>
            {(errors.length > 0 || failureMessages.length > 0) && (
              <>
                {errors.length > 0 && (
                  <ErrorMessage>
                    Something went wrong. Please refresh and try again.
                  </ErrorMessage>
                )}
                {failureMessages.length > 0 &&
                  failureMessages.map((fm) => (
                    <ErrorMessage key={fm.variantName}>
                      <p>{fm.message}</p>
                    </ErrorMessage>
                  ))}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                isDisabled={loading || submitting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                loadingText="Saving"
                isLoading={submitting}
                isDisabled={loading || submitting}
              >
                Save
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </FormWrapper>
    </Modal>
  );
};
